/**
 * File helpers for attachments.
 */

export const MAX_UPLOAD_BYTES = {
  image:    16 * 1024 * 1024,
  video:    64 * 1024 * 1024,
  audio:    16 * 1024 * 1024,
  document: 100 * 1024 * 1024
}

/** "2.4 MB" */
export function formatSize(bytes) {
  if (!bytes && bytes !== 0) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

/** Which attachment bucket a file belongs to. */
export function kindOf(file) {
  const type = file?.type ?? ''
  if (type.startsWith('image/')) return 'image'
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('audio/')) return 'audio'
  return 'document'
}

export function isWithinLimit(file) {
  return (file?.size ?? 0) <= MAX_UPLOAD_BYTES[kindOf(file)]
}

export function extensionOf(name = '') {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase()
}

/**
 * Shrink a photo before upload.
 *
 * Phone cameras produce 4-8MB images. Uploading them untouched is slow on
 * mobile data and multiplies storage costs for no visible benefit, so every
 * image goes through here first.
 *
 * @param {File} file
 * @param {{ maxDimension?: number, quality?: number }} [opts]
 * @returns {Promise<Blob>}
 */
export async function compressImage(file, { maxDimension = 1600, quality = 0.8 } = {}) {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file

  const bitmap = await createImageBitmap(file)
  const scale  = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height))
  if (scale === 1 && file.size < 1024 * 1024) {
    bitmap.close()
    return file
  }

  const width  = Math.round(bitmap.width * scale)
  const height = Math.round(bitmap.height * scale)
  const canvas = new OffscreenCanvas(width, height)
  canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height)
  bitmap.close()

  return canvas.convertToBlob({ type: 'image/jpeg', quality })
}

/** Pixel dimensions, stored as media metadata so the UI can reserve space. */
export async function imageDimensions(file) {
  try {
    const bitmap = await createImageBitmap(file)
    const size = { width: bitmap.width, height: bitmap.height }
    bitmap.close()
    return size
  } catch {
    return { width: 0, height: 0 }
  }
}
